import { useGlobal } from '@utmb/contexts/GlobalContext';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Image } from '@utmb/design-system/image/Image';
import { Link } from '@utmb/design-system/link/link';
import { Font } from '@utmb/design-system/text/font';
import { ColorTypesEnum } from '@utmb/types/colors';
import { LinkTypeEnum } from '@utmb/types/enum/LinkTypeEnum';
import { IconsEnum } from '@utmb/types/icons';
import SliceExpandableCardsCollectionCardView from '@utmb/types/view/SliceExpandableCardsCollectionCardView';
import { getColorStyle } from '@utmb/utils/colors';
import { parseMarkdown } from '@utmb/utils/markdown';
import React, { ReactElement } from 'react';
import styles from './expandable-cards-collection.module.scss';

interface ExpandableCardsCollectionModalProps {
    slices: SliceExpandableCardsCollectionCardView[];
    index: number;
    changeSlice: (index: number) => void;
    onClose: () => void;
}

const ExpandableCardsCollectionModal = (props: ExpandableCardsCollectionModalProps): ReactElement => {
    const { onClose, changeSlice, index, slices } = props;

    const {
        global: {
            event: { primaryColorIsDark },
        },
    } = useGlobal();

    const getPreviousSlice = (): number => {
        if (index <= 0) return slices.length - 1;
        return index - 1;
    };

    const getNextSlice = (): number => {
        if (index >= slices.length - 1) return 0;
        return index + 1;
    };

    return (
        <div className={styles.expandableCardCollectionModalContainer}>
            <button className={styles.button_close} onClick={onClose}>
                <Icon variant={IconsEnum.CLOSE} clss={['color-secondary', styles.button_close_icon].join(' ')} />
            </button>

            <Font.FuturaBold className={styles.title} desktop="30" mobile="24" as="h1">
                {slices[index].title}
            </Font.FuturaBold>

            {slices[index].picture && (
                <div className={styles.modalPicture}>
                    <Image image={slices[index].picture} layout="fill" objectFit="cover" />
                </div>
            )}

            <div className={styles.textContent}>
                <Font.FuturaBold className={styles.title} desktop="30" mobile="24" as="h1">
                    {slices[index].title}
                </Font.FuturaBold>
                <Font.FuturaHeavy className={styles.subTitle} desktop="24" mobile="20" as="h2">
                    {slices[index].subtitle}
                </Font.FuturaHeavy>
                <Font.FuturaMedium desktop="20" mobile="16" className={[styles.content, !slices[index].link ? styles.noLink : ''].join(' ')}>
                    {slices[index].content && (
                        <div
                            className={`markdown ${primaryColorIsDark ? 'primaryColorIsDark' : ''}`}
                            dangerouslySetInnerHTML={{ __html: parseMarkdown(slices[index].content) }}
                        />
                    )}
                </Font.FuturaMedium>
                <div className={styles.button_link}>
                    {slices[index].link &&
                        (slices[index].link.type === LinkTypeEnum.BUTTON ? (
                            <Link.Button
                                href={slices[index].link.slug}
                                label={slices[index].link.label}
                                followClassName={`${slices[index].link.followClass}-ga`}
                            />
                        ) : (
                            <Font.FuturaBook mobile="14" className={styles.content_link}>
                                <Link
                                    className="font-underline -stretched-link"
                                    href={slices[index].link.slug}
                                    followClassName={`${slices[index].link.followClass}-ga`}
                                >
                                    {slices[index].link.label}
                                </Link>
                            </Font.FuturaBook>
                        ))}
                </div>
                <div className={styles.nexPrevious}>
                    <div onClick={() => changeSlice(getPreviousSlice())} role={'none'}>
                        <Icon variant={IconsEnum.LEFT} color={getColorStyle(ColorTypesEnum.PRIMARY)} />
                    </div>

                    <div onClick={() => changeSlice(getNextSlice())} role={'none'}>
                        <Icon variant={IconsEnum.RIGHT} color={getColorStyle(ColorTypesEnum.PRIMARY)} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpandableCardsCollectionModal;
